/**
 * Выводы индикаторов потока внутри свечей и стакана.
 *
 * Кластеры, перекос, крупные объёмы, ликвидации и тепловая карта рисуют
 * много мелких фигур, и новичок в них тонет: где тут главное? Табло
 * отвечает одной фразой — что сказала последняя закрытая свеча, кто
 * давил последние двадцать свечей, где крупный игрок, — и называет
 * цифру, на которой вывод стоит.
 */

import type { Candle, OrderBookLive, OrderFlowVenue } from '@specter/types';
import type { FlowBarsSettings } from '../orderflow/bars-settings.js';
import { clusterOf, heavinessOf, HEAVINESS_TITLE, imbalancesOf, relativeVolume, stacksOf, typicalLevelVolume } from '../orderflow/bars.js';
import type { OrderFlowData } from '../orderflow/data.js';
import { money, signedMoney } from '../orderflow/settings.js';
import { closedIndex, percentWords, priceWords, situationRows, timesWords, whenWords, type Situation } from './explain.js';
import type { ReadoutRow } from './overlays.js';

/** Сколько свечей смотреть назад для выводов «за последнее время». */
const RECENT = 20;

function waiting(data: OrderFlowData | null | undefined): ReadoutRow[] | null {
  if (!data) return [];
  if (data.error) return [{ label: 'Поток', value: data.error, tone: 'muted', full: true }];
  if (!data.loaded) return [{ label: 'Поток', value: 'загружаю…', tone: 'muted' }];
  return null;
}

/* ---------------------------------------------------------------- кластеры */

export function explainClusters(
  candles: readonly Candle[],
  data: OrderFlowData | null | undefined,
  settings: FlowBarsSettings,
  livePartial: boolean,
): ReadoutRow[] {
  const early = waiting(data);
  if (early) return early;
  const i = closedIndex(candles, livePartial);
  const candle = candles[i];
  const bar = candle ? data!.bar(candle.time) : undefined;
  if (!candle || !bar || !(data!.step > 0)) return [{ label: 'Свеча', value: 'сделок ещё нет', tone: 'muted' }];

  const cluster = clusterOf(bar, data!.step, settings.venues, 1);
  const poc = cluster.poc;
  if (!poc) return [{ label: 'Свеча', value: 'сделок нет', tone: 'muted' }];

  const range = candle.high - candle.low;
  const pocMid = (poc.low + poc.high) / 2;
  const place = range > 0 ? (pocMid - candle.low) / range : 0.5;
  const upperWick = range > 0 ? (candle.high - Math.max(candle.open, candle.close)) / range : 0;
  const lowerWick = range > 0 ? (Math.min(candle.open, candle.close) - candle.low) / range : 0;
  const delta = cluster.buy - cluster.sell;
  const rising = candle.close > candle.open;

  let situation: Situation;
  if (place >= 0.7 && upperWick >= 0.3 && candle.close < pocMid) {
    situation = {
      title: 'Продавец наверху',
      tone: 'bear',
      text: 'Больше всего наторговали у максимума свечи, а закрылась она ниже — наверху цену встретил крупный продавец.',
    };
  } else if (place <= 0.3 && lowerWick >= 0.3 && candle.close > pocMid) {
    situation = {
      title: 'Покупатель внизу',
      tone: 'bull',
      text: 'Больше всего наторговали у минимума свечи, а закрылась она выше — внизу цену встретил крупный покупатель.',
    };
  } else if (delta > 0 && rising) {
    situation = { title: 'Покупатели вели свечу', tone: 'bull', text: 'По рынку больше покупали, и свеча закрылась ростом — движение поддержано сделками.' };
  } else if (delta < 0 && !rising) {
    situation = { title: 'Продавцы вели свечу', tone: 'bear', text: 'По рынку больше продавали, и свеча закрылась падением — движение поддержано сделками.' };
  } else if (delta > 0) {
    situation = {
      title: 'Покупки не помогли',
      tone: 'warn',
      text: 'Покупали больше, а свеча закрылась падением — покупки гасили продавцы заявками.',
    };
  } else {
    situation = {
      title: 'Продажи не помогли',
      tone: 'warn',
      text: 'Продавали больше, а свеча закрылась ростом — продажи забирали покупатели заявками.',
    };
  }

  const where = place >= 0.67 ? 'наверху свечи' : place <= 0.33 ? 'внизу свечи' : 'в середине свечи';
  return [
    ...situationRows(situation),
    { label: 'Главный уровень', value: `${priceWords(pocMid)} · ${where}` },
    { label: 'Перевес свечи', value: signedMoney(delta), tone: delta >= 0 ? 'up' : 'down' },
    { label: 'Свеча', value: `закрытая, ${whenWords(candle.time)}`, tone: 'muted' },
  ];
}

/* ---------------------------------------------------------------- перекос */

export function explainImbalance(
  candles: readonly Candle[],
  data: OrderFlowData | null | undefined,
  settings: FlowBarsSettings,
  livePartial: boolean,
): ReadoutRow[] {
  const early = waiting(data);
  if (early) return early;
  const last = closedIndex(candles, livePartial);
  const price = candles.at(-1)?.close;
  if (last < 0 || price === undefined || !(data!.step > 0)) return [];

  let buyStacks = 0;
  let sellStacks = 0;
  let nearest: { side: 'buy' | 'sell'; low: number; high: number; distance: number } | null = null;
  for (let i = Math.max(0, last - RECENT + 1); i <= last; i += 1) {
    const bar = data!.bar(candles[i]!.time);
    if (!bar) continue;
    const rows = clusterOf(bar, data!.step, settings.venues, 1).rows;
    for (const stack of stacksOf(imbalancesOf(rows, settings.ratio, settings.minVolume), settings.stack)) {
      if (stack.side === 'buy') buyStacks += 1;
      else sellStacks += 1;
      const mid = (stack.low + stack.high) / 2;
      const distance = Math.abs(mid - price) / price;
      if (!nearest || distance < nearest.distance) nearest = { side: stack.side, low: stack.low, high: stack.high, distance };
    }
  }

  let situation: Situation;
  if (buyStacks === 0 && sellStacks === 0) {
    situation = { title: 'Без давления', tone: 'muted', text: 'За последние 20 свечей ни одна сторона не продавливала стакан уверенно — стопок перекоса нет.' };
  } else if (buyStacks >= sellStacks * 2) {
    situation = {
      title: 'Давят покупатели',
      tone: 'bull',
      text: 'Покупатели чаще продавливали стакан по нескольку уровней подряд. Такие места — опоры, к которым цена часто возвращается.',
    };
  } else if (sellStacks >= buyStacks * 2) {
    situation = {
      title: 'Давят продавцы',
      tone: 'bear',
      text: 'Продавцы чаще продавливали стакан по нескольку уровней подряд. Такие места — потолки, к которым цена часто возвращается.',
    };
  } else {
    situation = { title: 'Давят обе стороны', tone: 'muted', text: 'Стопки перекоса есть с обеих сторон примерно поровну — явного хозяина у рынка нет.' };
  }

  const rows: ReadoutRow[] = [
    ...situationRows(situation),
    { label: 'Стопки за 20 свечей', value: `${buyStacks} покупок · ${sellStacks} продаж` },
  ];
  if (nearest) {
    const mid = (nearest.low + nearest.high) / 2;
    rows.push({
      label: 'Ближайшая стопка',
      value: `${priceWords(mid)} · ${nearest.side === 'buy' ? 'покупатели' : 'продавцы'} · ${percentWords(((mid - price) / price) * 100, 2)}`,
      tone: nearest.side === 'buy' ? 'up' : 'down',
    });
  }
  return rows;
}

/* ---------------------------------------------------------- крупные объёмы */

export function explainLarge(
  candles: readonly Candle[],
  data: OrderFlowData | null | undefined,
  settings: FlowBarsSettings,
  livePartial: boolean,
): ReadoutRow[] {
  const early = waiting(data);
  if (early) return early;
  const last = closedIndex(candles, livePartial);
  const price = candles.at(-1)?.close;
  if (last < 0 || price === undefined || !(data!.step > 0)) return [];

  // Обычный объём уровня — по сотне последних свечей, как у пузырей на экране.
  const clusters = [];
  for (let i = Math.max(0, last - 99); i <= last; i += 1) {
    const bar = data!.bar(candles[i]!.time);
    if (bar) clusters.push({ time: candles[i]!.time, profile: clusterOf(bar, data!.step, settings.venues, 1) });
  }
  const typical = typicalLevelVolume(clusters.map((item) => item.profile));

  let found: { time: number; price: number; relative: number; buy: boolean } | null = null;
  for (const { time, profile } of clusters.slice(-RECENT)) {
    for (const row of profile.rows) {
      const relative = relativeVolume(row, typical);
      if (relative < settings.large) continue;
      if (!found || time > found.time || (time === found.time && relative > found.relative)) {
        found = { time, price: (row.low + row.high) / 2, relative, buy: row.buy >= row.sell };
      }
    }
  }

  let situation: Situation;
  if (!found) {
    situation = { title: 'Без выбросов', tone: 'muted', text: 'За последние 20 свечей крупных выбросов объёма не было — никто не заходил большим размером.' };
  } else if (found.buy && found.price <= price) {
    situation = {
      title: 'Крупный покупатель ниже',
      tone: 'bull',
      text: 'Ниже цены прошёл крупный объём с перевесом покупок. Такой уровень часто становится опорой: покупатель, скорее всего, будет его защищать.',
    };
  } else if (!found.buy && found.price >= price) {
    situation = {
      title: 'Крупный продавец выше',
      tone: 'bear',
      text: 'Выше цены прошёл крупный объём с перевесом продаж. Такой уровень часто становится потолком: продавец, скорее всего, будет его защищать.',
    };
  } else {
    situation = {
      title: found.buy ? 'Крупные покупки пробиты' : 'Крупные продажи пробиты',
      tone: 'warn',
      text: found.buy
        ? 'Крупные покупки были выше нынешней цены — покупатель, зашедший там, сейчас в минусе. Если цена вернётся к уровню, он может закрываться.'
        : 'Крупные продажи были ниже нынешней цены — продавец, зашедший там, сейчас в минусе. Если цена вернётся к уровню, он может закрываться.',
    };
  }

  const rows: ReadoutRow[] = [...situationRows(situation)];
  if (found) {
    const heaviness = heavinessOf(found.relative);
    rows.push({
      label: 'Последний крупный',
      value: `${priceWords(found.price)} · ${timesWords(found.relative)}${heaviness ? ` · ${HEAVINESS_TITLE[heaviness]}` : ''}`,
      tone: found.buy ? 'up' : 'down',
    });
    rows.push({ label: 'Когда', value: whenWords(found.time), tone: 'muted' });
  }
  return rows;
}

/* ------------------------------------------------------ ликвидации на цене */

export function explainFlowLiquidations(
  candles: readonly Candle[],
  data: OrderFlowData | null | undefined,
  venues: ReadonlySet<OrderFlowVenue>,
  livePartial: boolean,
): ReadoutRow[] {
  const early = waiting(data);
  if (early) return early;
  const last = closedIndex(candles, livePartial);
  if (last < 0) return [];

  let longs = 0;
  let shorts = 0;
  let biggest: { price: number; usd: number; long: boolean } | null = null;
  for (let i = Math.max(0, last - RECENT + 1); i <= last; i += 1) {
    const bar = data!.bar(candles[i]!.time);
    if (!bar) continue;
    for (const [venue, levels] of bar.liq) {
      if (!venues.has(venue)) continue;
      for (const [level, cell] of levels) {
        const long = cell[0] ?? 0;
        const short = cell[1] ?? 0;
        longs += long;
        shorts += short;
        const usd = long + short;
        if (!biggest || usd > biggest.usd) biggest = { price: (level + 0.5) * data!.step, usd, long: long >= short };
      }
    }
  }

  const total = longs + shorts;
  let situation: Situation;
  if (total <= 0) {
    situation = { title: 'Тихо', tone: 'muted', text: 'За последние 20 свечей позиции почти не выносило.' };
  } else if (longs >= shorts * 2) {
    situation = {
      title: 'Выносит длинных',
      tone: 'bear',
      text: 'Ставивших на рост выносит чаще: их позиции закрываются принудительными продажами, и это ускоряет падение. После крупной волны часто бывает отскок.',
    };
  } else if (shorts >= longs * 2) {
    situation = {
      title: 'Выносит коротких',
      tone: 'bull',
      text: 'Ставивших на падение выносит чаще: их позиции закрываются принудительными покупками, и это ускоряет рост. После крупной волны часто бывает откат.',
    };
  } else {
    situation = { title: 'Выносит обе стороны', tone: 'muted', text: 'Позиции выносит в обе стороны примерно поровну — рынок дёргает туда-сюда.' };
  }

  const rows: ReadoutRow[] = [
    ...situationRows(situation),
    { label: 'За 20 свечей', value: `длинных ${money(longs)} · коротких ${money(shorts)}` },
  ];
  if (biggest && biggest.usd > 0) {
    rows.push({ label: 'Крупнейший вынос', value: `${priceWords(biggest.price)} · ${money(biggest.usd)}`, tone: biggest.long ? 'down' : 'up' });
  }
  return rows;
}

/* ---------------------------------------------------------- тепловая карта */

/** Насколько далеко от цены смотреть стены для табло: два процента. */
const BOOK_REACH = 0.02;

export function explainBook(
  price: number | undefined,
  live: OrderBookLive | null | undefined,
  venues: ReadonlySet<OrderFlowVenue>,
  factor: number,
): ReadoutRow[] {
  if (!live || !(live.step > 0) || price === undefined) {
    return [{ label: 'Стакан', value: 'ждём живой снимок…', tone: 'muted' }];
  }
  const sum = (side: 'b' | 'a'): Map<number, number> => {
    const byLevel = new Map<number, number>();
    for (const [venue, book] of Object.entries(live.v) as Array<[OrderFlowVenue, NonNullable<OrderBookLive['v'][OrderFlowVenue]>]>) {
      if (!venues.has(venue) || !book) continue;
      for (const [level, usd] of book[side]) {
        const at = (level + 0.5) * live.step;
        if (Math.abs(at - price) / price > BOOK_REACH) continue;
        byLevel.set(level, (byLevel.get(level) ?? 0) + usd);
      }
    }
    return byLevel;
  };
  const bids = sum('b');
  const asks = sum('a');
  const all = [...bids.values(), ...asks.values()].sort((a, b) => a - b);
  if (all.length === 0) return [{ label: 'Стакан', value: 'рядом с ценой пусто', tone: 'muted' }];
  const typical = all[Math.floor(all.length / 2)]!;

  const top = (levels: Map<number, number>) => {
    let best: { level: number; usd: number } | null = null;
    for (const [level, usd] of levels) if (!best || usd > best.usd) best = { level, usd };
    return best && typical > 0 && best.usd / typical >= factor ? best : null;
  };
  const bid = top(bids);
  const ask = top(asks);

  let situation: Situation;
  if (bid && (!ask || bid.usd >= ask.usd * 1.5)) {
    situation = {
      title: 'Снизу плотнее',
      tone: 'bull',
      text: 'Под ценой стоит крупная заявка на покупку — пока она стоит, падать цене труднее. Заявку могут снять в любой момент, поэтому это опора, а не гарантия.',
    };
  } else if (ask && (!bid || ask.usd >= bid.usd * 1.5)) {
    situation = {
      title: 'Сверху плотнее',
      tone: 'bear',
      text: 'Над ценой стоит крупная заявка на продажу — пока она стоит, расти цене труднее. Заявку могут снять в любой момент, поэтому это потолок, а не гарантия.',
    };
  } else if (bid && ask) {
    situation = { title: 'Цена зажата', tone: 'warn', text: 'Крупные заявки стоят и снизу, и сверху — цена зажата между ними. Пробой одной из стен обычно резкий.' };
  } else {
    situation = { title: 'Стакан ровный', tone: 'muted', text: 'Рядом с ценой нет заявок заметно крупнее обычных — стен нет.' };
  }

  const rows: ReadoutRow[] = [...situationRows(situation)];
  const describe = (wall: { level: number; usd: number }) =>
    `${priceWords((wall.level + 0.5) * live.step)} · ${money(wall.usd)} · ${timesWords(wall.usd / typical)}`;
  if (ask) rows.push({ label: 'Стена сверху', value: describe(ask), tone: 'down' });
  if (bid) rows.push({ label: 'Стена снизу', value: describe(bid), tone: 'up' });
  rows.push({ label: 'Смотрим', value: 'живой стакан ±2% от цены', tone: 'muted' });
  return rows;
}

